import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';

import { api } from '@/api/client';
import { PageHeader } from '@/components/ui/PageHeader';
import { cn } from '@/lib/utils';

/**
 * Экран «Сервер»: память, диск, процессор, сеть — с историей и пиками.
 *
 * ⚠️ ЧЕМ ОН ОТЛИЧАЕТСЯ ОТ «ЗДОРОВЬЯ ПЛАТФОРМЫ». Тот отвечает на вопрос «работает
 * ли сейчас», этот — на «когда прижало и упрёмся ли». Заказчик 07.09.2026 просил
 * «графики, пики, всплески»: беда почти всегда позади, и «в шесть утра всё
 * тормозило» видно только на графике.
 *
 * ⚠️ ГРАФИКИ РИСУЕМ САМИ, БЕЗ БИБЛИОТЕКИ. Тянуть ради четырёх линий стороннюю
 * библиотеку — это лишние сотни килобайт в сборку, которую грузят с телефона
 * восемь из десяти учеников. Линия в SVG — тридцать строк.
 */

type Point = {
  at: string;
  cpu_percent: number;
  mem_used_mb: number;
  mem_total_mb: number;
  disk_used_gb: number;
  disk_total_gb: number;
  disks?: { path: string; used_gb: number; total_gb: number }[];
  load1: number;
  net_rx_kbs: number;
  net_tx_kbs: number;
};

type Metrics = {
  points: Point[];
  peak_cpu: number;
  peak_mem_mb: number;
  peak_load: number;
  cores: number;
};

/**
 * Скорость сети по-человечески.
 *
 * ⚠️ ОКРУГЛЕНИЕ ДО ЦЕЛЫХ КИЛОБАЙТ ВРАЛО. Заказчик 07.09.2026 увидел «0 КБ/с» и
 * спросил, не лёг ли сервер. Сервер работал: трафик был 0,38 КБ/с — реальный, но
 * при округлении до целого превращался в ноль. Ночью и в затишье такое сплошь и
 * рядом, и каждый раз это выглядит как отказ.
 *
 * Поэтому: до килобайта показываем байты, до мегабайта — килобайты с десятой
 * долей, дальше — мегабайты. Ноль на экране теперь означает настоящий ноль.
 */
function speedText(kbs: number): string {
  if (kbs <= 0) return '0 Б/с';
  if (kbs < 1) return `${Math.round(kbs * 1024)} Б/с`;
  if (kbs < 1024) return `${kbs < 10 ? kbs.toFixed(1) : Math.round(kbs)} КБ/с`;
  return `${(kbs / 1024).toFixed(1)} МБ/с`;
}

const RANGES = [
  { hours: 1, key: 'serverPage.range1h' },
  { hours: 24, key: 'serverPage.range24h' },
  { hours: 24 * 7, key: 'serverPage.range7d' },
];

/**
 * Линия графика.
 *
 * ⚠️ ШКАЛА ВСЕГДА ОТ НУЛЯ. Подгонять низ под минимум удобно для «красивой»
 * линии, но обманывает: колебание памяти в один процент растянется на всю высоту
 * и будет выглядеть как обвал. От нуля видно настоящий масштаб.
 */
function Chart({
  points,
  value,
  max,
  color,
  unit,
}: {
  points: Point[];
  value: (p: Point) => number;
  max: number;
  color: string;
  unit: string;
}) {
  const { i18n } = useTranslation();
  if (points.length < 2) {
    return <div className="h-[120px] rounded-[14px] bg-surface-container-high" />;
  }

  const W = 1000;
  const H = 120;
  const top = Math.max(max, 1);
  const x = (i: number) => (i / (points.length - 1)) * W;
  const y = (v: number) => H - Math.min(v, top) / top * (H - 8) - 4;

  const line = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${x(i).toFixed(1)},${y(value(p)).toFixed(1)}`).join(' ');
  const area = `${line} L${W},${H} L0,${H} Z`;

  const last = points[points.length - 1];
  const first = points[0];
  const time = (s: string) =>
    new Date(s).toLocaleTimeString(i18n.language, { hour: '2-digit', minute: '2-digit' });

  return (
    <div>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" className="h-[120px] w-full">
        {/* Три опорные линии: без них глаз не оценивает высоту всплеска. */}
        {[0.25, 0.5, 0.75].map((f) => (
          <line key={f} x1="0" x2={W} y1={H * f} y2={H * f} stroke="currentColor" strokeWidth="1" className="text-outline" opacity="0.35" />
        ))}
        <path d={area} fill={color} opacity="0.12" />
        <path d={line} fill="none" stroke={color} strokeWidth="2.5" vectorEffect="non-scaling-stroke" />
      </svg>
      <div className="mt-1 flex justify-between text-[11px] tabular-nums text-on-surface-variant">
        <span>{time(first.at)}</span>
        <span>
          {value(last).toFixed(unit === '%' ? 0 : 1)} {unit}
        </span>
        <span>{time(last.at)}</span>
      </div>
    </div>
  );
}

function Card({
  title,
  now,
  peak,
  children,
  tone,
}: {
  title: string;
  now: string;
  peak?: string;
  children: React.ReactNode;
  tone?: 'warn' | 'crit';
}) {
  return (
    <div className="rounded-[20px] bg-surface-container px-[18px] py-4">
      <div className="mb-2 flex items-baseline gap-3">
        <h3 className="flex-1 font-display text-[14.5px] font-bold text-on-surface">{title}</h3>
        <span
          className={cn(
            'text-[15px] font-bold tabular-nums',
            tone === 'crit' ? 'text-error' : tone === 'warn' ? 'text-warning' : 'text-on-surface',
          )}
        >
          {now}
        </span>
      </div>
      {peak && <div className="mb-2 text-[11.5px] text-on-surface-variant">{peak}</div>}
      {children}
    </div>
  );
}

/**
 * Журнал служб: предупреждения и ошибки со всей платформы.
 *
 * ⚠️ ЭТО НЕ «АБСОЛЮТНО ВСЕ ЛОГИ», И ЭТО ОСОЗНАННЫЙ ВЫБОР. Полный поток
 * семнадцати служб — десятки гигабайт в неделю, из которых читают полстроки.
 * Сюда попадает то, ради чего журнал открывают: что сломалось. Полная запись
 * по-прежнему лежит на сервере.
 */
function Logs() {
  const { t, i18n } = useTranslation();
  const [service, setService] = useState('');
  const [level, setLevel] = useState('');
  const [q, setQ] = useState('');

  const logs = useQuery({
    queryKey: ['admin', 'server-logs', service, level, q],
    queryFn: async () => {
      const p = new URLSearchParams();
      if (service) p.set('service', service);
      if (level) p.set('level', level);
      if (q) p.set('q', q);
      const { data } = await api.get(`/admin/platform/logs?${p.toString()}`);
      return data as { entries: LogEntry[]; services: string[] };
    },
    refetchInterval: 30_000,
  });

  const d = logs.data;

  return (
    <div className="mt-3.5 rounded-[20px] bg-surface-container px-[18px] py-4">
      <h3 className="mb-3 font-display text-[14.5px] font-bold text-on-surface">
        {t('serverPage.logsTitle')}
      </h3>

      <div className="mb-3 flex flex-wrap gap-2">
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder={t('serverPage.logsSearch')}
          className="min-w-[180px] flex-1 rounded-full bg-surface-container-high px-4 py-2 text-[12.5px] text-on-surface outline-none"
        />
        <select
          value={service}
          onChange={(e) => setService(e.target.value)}
          className="rounded-full bg-surface-container-high px-3.5 py-2 text-[12.5px] text-on-surface outline-none"
        >
          <option value="">{t('serverPage.logsAllServices')}</option>
          {(d?.services ?? []).map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select
          value={level}
          onChange={(e) => setLevel(e.target.value)}
          className="rounded-full bg-surface-container-high px-3.5 py-2 text-[12.5px] text-on-surface outline-none"
        >
          <option value="">{t('serverPage.logsAllLevels')}</option>
          <option value="info">info</option>
          <option value="warn">warn</option>
          <option value="error">error</option>
          <option value="fatal">fatal</option>
        </select>
      </div>

      {d && d.entries.length === 0 && (
        <p className="py-4 text-center text-[12.5px] text-on-surface-variant">
          {t('serverPage.logsEmpty')}
        </p>
      )}

      <div className="max-h-[460px] overflow-y-auto">
        {(d?.entries ?? []).map((e) => (
          <div key={e.id} className="border-b border-outline py-2 last:border-b-0">
            <div className="flex items-baseline gap-2">
              <span className="flex-none text-[11px] tabular-nums text-on-surface-variant">
                {new Date(e.at).toLocaleString(i18n.language, {
                  day: '2-digit', month: '2-digit',
                  hour: '2-digit', minute: '2-digit', second: '2-digit',
                })}
              </span>
              {/* ⚠️ КРАСНЫМ — ТОЛЬКО НАСТОЯЩИЕ ОШИБКИ. Первая редакция красила
                  всё, что не «warn», то есть и обычные события тоже: журнал
                  выглядел сплошной бедой, хотя девять записей из десяти — это
                  спокойный ход работы. Заказчик 07.09.2026 посмотрел и сказал:
                  «мне кажется, это всё ошибки». Так и выглядело — по моей
                  вине. */}
              <span
                className={cn(
                  'flex-none rounded-full px-2 py-0.5 text-[10px] font-bold uppercase',
                  e.level === 'error' || e.level === 'fatal'
                    ? 'bg-error-container text-error'
                    : e.level === 'warn'
                      ? 'bg-warning-container text-warning'
                      : 'bg-surface-container-high text-on-surface-variant',
                )}
              >
                {e.level}
              </span>
              <span className="flex-none text-[11px] font-semibold text-on-surface-variant">
                {e.service}
              </span>
            </div>
            {/* Текст записи ломаем по словам: в сообщениях встречаются длинные
                пути и запросы, и без переноса они растягивали бы экран вбок. */}
            <p className="mt-0.5 whitespace-pre-wrap break-words text-[12.5px] text-on-surface">
              {e.message}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

type LogEntry = { id: number; at: string; service: string; level: string; message: string };

export function AdminServerPage() {
  const { t } = useTranslation();
  const [hours, setHours] = useState(24);

  const q = useQuery({
    queryKey: ['admin', 'server-metrics', hours],
    queryFn: async () => {
      const { data } = await api.get(`/admin/platform/metrics?hours=${hours}&points=180`);
      return data as Metrics;
    },
    // Точка снимается раз в минуту — чаще обновлять нечего.
    refetchInterval: 60_000,
  });

  const d = q.data;
  const pts = d?.points ?? [];
  const last = pts[pts.length - 1];

  const memPct = last && last.mem_total_mb ? (last.mem_used_mb / last.mem_total_mb) * 100 : 0;
  const diskPct = last && last.disk_total_gb ? (last.disk_used_gb / last.disk_total_gb) * 100 : 0;

  /** Выше этого — стоит смотреть; выше второго — пора действовать. */
  const tone = (pct: number) => (pct >= 90 ? 'crit' : pct >= 75 ? 'warn' : undefined);

  return (
    <>
      <PageHeader title={t('serverPage.title')} subtitle={t('serverPage.subtitle')} />

      <div className="mb-3.5 flex gap-1.5">
        {RANGES.map((r) => (
          <button
            key={r.hours}
            type="button"
            onClick={() => setHours(r.hours)}
            className={cn(
              'rounded-full px-4 py-2 text-[12.5px] font-semibold transition',
              hours === r.hours
                ? 'bg-primary text-on-primary'
                : 'bg-surface-container text-on-surface-variant hover:bg-surface-container-high',
            )}
          >
            {t(r.key)}
          </button>
        ))}
      </div>

      {q.isLoading && (
        <p className="py-6 text-center text-[13px] text-on-surface-variant">{t('common.loading')}</p>
      )}

      {q.isError && (
        <div className="rounded-[20px] bg-error-container px-[18px] py-4 text-[13px] text-on-error-container">
          {t('serverPage.error')}
        </div>
      )}

      {d && pts.length === 0 && (
        <div className="rounded-[20px] bg-surface-container px-[18px] py-4 text-[13px] text-on-surface-variant">
          {/* Первые точки появятся через минуту после запуска службы: копить
              историю задним числом неоткуда. */}
          {t('serverPage.noData')}
        </div>
      )}

      {d && pts.length > 0 && last && (
        <div className="grid gap-3.5 lg:grid-cols-2">
          <Card
            title={t('serverPage.cpu', { cores: d.cores })}
            now={`${last.cpu_percent.toFixed(0)}%`}
            peak={t('serverPage.peak', { value: `${d.peak_cpu.toFixed(0)}%` })}
            tone={tone(last.cpu_percent)}
          >
            <Chart points={pts} value={(p) => p.cpu_percent} max={100} color="#E07B00" unit="%" />
          </Card>

          <Card
            title={t('serverPage.memory')}
            now={`${(last.mem_used_mb / 1024).toFixed(1)} / ${(last.mem_total_mb / 1024).toFixed(0)} ГБ`}
            peak={t('serverPage.peak', { value: `${(d.peak_mem_mb / 1024).toFixed(1)} ГБ` })}
            tone={tone(memPct)}
          >
            <Chart
              points={pts}
              value={(p) => p.mem_used_mb / 1024}
              max={(last.mem_total_mb || 1) / 1024}
              color="#2563EB"
              unit="ГБ"
            />
          </Card>

          <Card
            title={t('serverPage.disk')}
            now={`${last.disk_used_gb} / ${last.disk_total_gb} ГБ · ${diskPct.toFixed(0)}%`}
            peak={t('serverPage.diskFree', { value: last.disk_total_gb - last.disk_used_gb })}
            tone={tone(diskPct)}
          >
            <Chart
              points={pts}
              value={(p) => p.disk_used_gb}
              max={last.disk_total_gb || 1}
              color="#2A7A52"
              unit="ГБ"
            />

            {/* ⚠️ РАЗДЕЛОВ НЕСКОЛЬКО, И ГЛАВНЫЙ — НЕ СИСТЕМНЫЙ. График выше
                показывает «/», где живут система и докер. Но рядом стоит массив
                /data почти на терабайт, куда складываются файлы учеников, — и
                без этой строки казалось, что места в обрез, хотя свободно
                девятьсот гигабайт. Заказчик 07.09.2026: «у нас же 4 диска,
                почему только 1 показываешь?». */}
            {!!last.disks?.length && (
              <div className="mt-3 border-t border-outline pt-2.5">
                {last.disks.map((d) => {
                  const pct = d.total_gb ? (d.used_gb / d.total_gb) * 100 : 0;
                  return (
                    <div key={d.path} className="flex items-center gap-3 py-1">
                      <span className="w-[70px] flex-none truncate text-[12px] font-semibold text-on-surface">
                        {d.path}
                      </span>
                      <span className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-container-high">
                        <span
                          className={cn(
                            'block h-full rounded-full',
                            pct >= 90 ? 'bg-error' : pct >= 75 ? 'bg-warning' : 'bg-success',
                          )}
                          style={{ width: `${Math.max(pct, 1)}%` }}
                        />
                      </span>
                      <span className="flex-none text-[11.5px] tabular-nums text-on-surface-variant">
                        {d.used_gb} / {d.total_gb} ГБ
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </Card>

          <Card
            title={t('serverPage.load')}
            now={last.load1.toFixed(2)}
            peak={t('serverPage.loadHint', { peak: d.peak_load.toFixed(2), cores: d.cores })}
            tone={last.load1 > d.cores ? 'crit' : last.load1 > d.cores * 0.7 ? 'warn' : undefined}
          >
            <Chart points={pts} value={(p) => p.load1} max={Math.max(d.peak_load, d.cores / 4)} color="#9A6700" unit="" />
          </Card>

          <Card
            title={t('serverPage.netIn')}
            now={speedText(last.net_rx_kbs)}
            peak={t('serverPage.netHint')}
          >
            <Chart
              points={pts}
              value={(p) => p.net_rx_kbs}
              max={Math.max(...pts.map((p) => p.net_rx_kbs), 1)}
              color="#7C3AED"
              unit="КБ/с"
            />
          </Card>

          <Card title={t('serverPage.netOut')} now={speedText(last.net_tx_kbs)}>
            <Chart
              points={pts}
              value={(p) => p.net_tx_kbs}
              max={Math.max(...pts.map((p) => p.net_tx_kbs), 1)}
              color="#C0392B"
              unit="КБ/с"
            />
          </Card>
        </div>
      )}

      <Logs />
    </>
  );
}
